import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * ProfileMenu — top-right account control for the admin layout.
 *
 * A circular avatar button (operator's initial) opens a small dropdown with
 * the signed-in email, "Connected accounts" + "Security" (NEXUS-586), "Change password", and "Sign out". Rendered only when
 * the host SPA wires up the provider's `userEmail` / `onSignOut` (so other
 * admin SPAs that don't pass them are unaffected).
 *
 * Self-contained: React state drives the dropdown + modal (no Bootstrap JS
 * dependency — host SPAs only ship the CSS). Change password posts to the
 * gateway's authed `/auth/change-password` via nexusFetch (bearer attached).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNexusAdmin } from '../provider.js';
import { useNexusFetch, NexusAdminApiError } from '../api.js';
import { PasswordField } from '../components/PasswordField.js';
export function ProfileMenu({ basePath } = {}) {
    const { userEmail, onSignOut } = useNexusAdmin();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const ref = useRef(null);
    // Close the dropdown on outside-click / Escape.
    useEffect(() => {
        if (!open)
            return;
        const onDown = (e) => {
            if (ref.current && !ref.current.contains(e.target))
                setOpen(false);
        };
        const onKey = (e) => e.key === 'Escape' && setOpen(false);
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
    // Nothing to render unless the host opted in (after hooks — rules-of-hooks).
    if (!userEmail && !onSignOut)
        return null;
    const initial = (userEmail?.trim()?.[0] ?? '?').toUpperCase();
    return (_jsxs("div", { className: "position-relative", ref: ref, children: [_jsx("button", { type: "button", className: "btn btn-light rounded-circle border d-flex align-items-center justify-content-center p-0", style: { width: 38, height: 38, fontWeight: 600 }, title: userEmail ?? 'Account', "aria-haspopup": "menu", "aria-expanded": open, onClick: () => setOpen((v) => !v), children: initial }), open && (_jsxs("div", { className: "position-absolute end-0 mt-2 bg-white border rounded shadow-sm", role: "menu", style: { minWidth: 220, zIndex: 1050 }, children: [userEmail && (_jsxs("div", { className: "px-3 py-2 border-bottom", children: [_jsx("div", { className: "text-muted small", children: "Signed in as" }), _jsx("div", { className: "text-truncate fw-semibold", style: { maxWidth: 200 }, children: userEmail })] })), basePath !== undefined && (_jsx("button", { type: "button", className: "btn btn-link text-decoration-none text-dark d-block w-100 text-start px-3 py-2", role: "menuitem", onClick: () => {
                            setOpen(false);
                            navigate(`${basePath}/account`);
                        }, children: "Connected accounts" })), basePath !== undefined && (_jsx("button", { type: "button", className: "btn btn-link text-decoration-none text-dark d-block w-100 text-start px-3 py-2", role: "menuitem", onClick: () => {
                            setOpen(false);
                            navigate(`${basePath}/security`);
                        }, children: "Security" })), _jsx("button", { type: "button", className: "btn btn-link text-decoration-none text-dark d-block w-100 text-start px-3 py-2", role: "menuitem", onClick: () => {
                            setOpen(false);
                            setShowModal(true);
                        }, children: "Change password" }), onSignOut && (_jsx("button", { type: "button", className: "btn btn-link text-decoration-none text-danger d-block w-100 text-start px-3 py-2 border-top", role: "menuitem", onClick: () => {
                            setOpen(false);
                            onSignOut();
                        }, children: "Sign out" }))] })), showModal && _jsx(ChangePasswordModal, { onClose: () => setShowModal(false) })] }));
}
function ChangePasswordModal({ onClose }) {
    const fetcher = useNexusFetch();
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(false);
    const submit = useCallback(async () => {
        setError(null);
        if (next.length < 8) {
            setError('New password must be at least 8 characters.');
            return;
        }
        if (next !== confirm) {
            setError('New password and confirmation do not match.');
            return;
        }
        setBusy(true);
        try {
            await fetcher('/auth/change-password', {
                method: 'POST',
                json: { currentPassword: current, newPassword: next },
            });
            setDone(true);
        }
        catch (e) {
            if (e instanceof NexusAdminApiError) {
                if (e.status === 401)
                    setError('Current password is incorrect.');
                else if (e.code === 'WEAK_PASSWORD')
                    setError(e.errors?.map((x) => x.message).join('; ') || 'New password does not meet policy.');
                else
                    setError(e.message);
            }
            else {
                setError(e instanceof Error ? e.message : 'Could not change password.');
            }
        }
        finally {
            setBusy(false);
        }
    }, [fetcher, current, next, confirm]);
    return (_jsx("div", { className: "position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center", style: { background: 'rgba(0,0,0,0.4)', zIndex: 1060 }, onMouseDown: (e) => {
            if (e.target === e.currentTarget && !busy)
                onClose();
        }, children: _jsxs("div", { className: "bg-white rounded shadow p-4", style: { width: 380, maxWidth: '92vw' }, children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-3", children: [_jsx("h5", { className: "m-0", children: "Change password" }), _jsx("button", { type: "button", className: "btn-close", "aria-label": "Close", onClick: onClose, disabled: busy })] }), done ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "alert alert-success py-2", children: "Password changed." }), _jsx("div", { className: "text-end", children: _jsx("button", { type: "button", className: "btn btn-primary", onClick: onClose, children: "Done" }) })] })) : (_jsxs("form", { onSubmit: (e) => {
                        e.preventDefault();
                        if (!busy)
                            void submit();
                    }, children: [error && _jsx("div", { className: "alert alert-danger py-2", children: error }), _jsxs("div", { className: "mb-2", children: [_jsx("label", { className: "form-label small mb-1", children: "Current password" }), _jsx(PasswordField, { className: "form-control", value: current, autoFocus: true, autoComplete: "current-password", onChange: (e) => setCurrent(e.target.value) })] }), _jsxs("div", { className: "mb-2", children: [_jsx("label", { className: "form-label small mb-1", children: "New password" }), _jsx(PasswordField, { className: "form-control", value: next, autoComplete: "new-password", onChange: (e) => setNext(e.target.value) })] }), _jsxs("div", { className: "mb-2", children: [_jsx("label", { className: "form-label small mb-1", children: "Confirm new password" }), _jsx(PasswordField, { className: "form-control", value: confirm, autoComplete: "new-password", onChange: (e) => setConfirm(e.target.value) })] }), _jsx("div", { className: "mb-3" }), _jsxs("div", { className: "d-flex justify-content-end gap-2", children: [_jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: onClose, disabled: busy, children: "Cancel" }), _jsx("button", { type: "submit", className: "btn btn-primary", disabled: busy, children: busy ? 'Changing…' : 'Change password' })] })] }))] }) }));
}
//# sourceMappingURL=ProfileMenu.js.map